import { captureException } from '@sentry/react';
import axios from 'axios';
import { Group } from 'core';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import uri from 'uri-tag';
import { useCache } from '../../../providers/cache.js';
import type { ExistingEntityViewProps } from '../../../utils/types.js';
import { formikCheck } from '../../../utils/validation.js';
import { EditDetailsView } from './edit-details.js';

export const EditDetails: FC<ExistingEntityViewProps<Group.Type>> = ({
  id,
  data,
  refresh,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { group } = useCache();

  const formik = useFormik<Group.Patch>({
    initialValues: {
      title: data.title,
      tags: data.tags ?? [],
      labels: data.labels ?? {},
    },
    validate: async (value) => formikCheck(Group.patchContract, value),
    validateOnBlur: true,
    onReset: refresh,
    onSubmit: async (body) => {
      try {
        await axios.patch(uri`/api/v1/group/${id}`, body);
        enqueueSnackbar('Group updated', { variant: 'success' });
      } catch (err: unknown) {
        captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to update: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
      }
      group.invalidate(id);
      refresh();
    },
  });

  return EditDetailsView({
    formik,
  });
};
